import { Modal, Switch } from 'antd';
import type { WithControlPropsType } from '@ant-design/pro-components';
import { ProForm, useControlModel } from '@ant-design/pro-components';
import type { Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import { useContactsContext } from '@/contexts/contacts/context';
import type { CreateContactParams } from '@/contexts/contacts/types';
import styles from './index.module.scss';
import GoIcon from './images/go.svg?react';
import CloseIcon from './images/close.svg?react';
import TagIcon from './images/tag.svg?react';
import AddIcon from './images/add.svg?react';
import SelIcon from './images/sel.svg?react';
import UnSelIcon from './images/unsel.svg?react';

export interface ChatProps {
  chat: Chat;
  open: boolean;
  setOpen: (open: boolean) => void;
}

enum Step {
  NewContact = 'newContact',
  ChooseTag = 'chooseTag',
  CreateTag = 'createTag',
}

const CustomInputTag = (
  props: WithControlPropsType<{
    goNext: () => void;
  }>
) => {
  const model = useControlModel(props);
  return (
    <div className={cls(styles.align_center, styles.tags)} onClick={props.goNext}>
      <Input {...model} />
      <GoIcon />
    </div>
  );
};
const CustomSwitch = (
  props: WithControlPropsType<{
    goNext: () => void;
  }>
) => {
  const model = useControlModel(props);
  return (
    <div className={styles.align_center}>
      <span>Follow her/his feeds, shorts, live, etc.</span>
      <Switch {...model} />
    </div>
  );
};

const AddFriendModal = ({ chat, open, setOpen }: ChatProps) => {
  const [addTagForm] = Form.useForm();
  const [newContactForm] = Form.useForm();
  const { addContacts } = useContactsContext();
  const [currentStep, setCurrentStep] = useState<Step>(Step.NewContact);
  const [tagList, setTagList] = useState([
    {
      title: 'UI Designer',
      id: 1,
      choose: true,
    },
    {
      title: 'Product Manager',
      id: 2,
      choose: false,
    },
  ]);

  const handleTagClick = (id: number) => {
    setTagList((prevTagList) =>
      prevTagList.map((item) =>
        item.id === id ? { ...item, choose: !item.choose } : item
      )
    );
  };

  const handleConfirmTags = () => {
    setCurrentStep(Step.NewContact);
    const selectedTags = tagList
      .filter((item) => item.choose)
      .map((item) => item.title);
    newContactForm.setFieldsValue({ tags: selectedTags.join(', ') });
  };

  const handleCreateTag = () => {
    addTagForm
      .validateFields()
      .then((values) => {
        const newTag = {
          title: values.tag_name,
          id: tagList.length + 1,
          choose: true,
        };
        setTagList((prevTagList) => [...prevTagList, newTag]);
        setCurrentStep(Step.ChooseTag);
      })
      .catch(() => {});
  };

  const addContactClick = useCallback(
    async (values: any) => {
      const { user } = chat;
      const params: CreateContactParams = {
        chat_id: chat.chat_id,
        id: 0,
        category: 0,
        user_id: user.userId,
        state_msg: values.text || '',
        name: user.name || '',
        username: user.username || '',
        avatar: user.avatar!,
        remark_name: values.alias || '',
        verified: 0,
        dnd: 0,
        org_id: 0,
        org_name: '',
        external: 0,
        role: 0,
        create_at: user.createdAt!,
        update_at: user.updatedAt!,
      };
      await addContacts([params]);
      setOpen(false);
      // message.success('Contact request sent successfully');
    },
    [addContacts, chat, setOpen]
  );

  return (
    <Modal
      destroyOnHidden
      footer={null}
      open={open}
      width={666}
      title={null}
      closable={false}
      maskClosable={false}
      className={styles.contact_modal}
      onCancel={() => setOpen(false)}
    >
      <div className={styles.wrapper}>
        {currentStep === Step.NewContact && (
          <>
            <div className={styles.header}>
              <span>Add Contacts</span>
              <CloseIcon onClick={() => setOpen(false)} />
            </div>
            <div className={styles.p_container}>
              <MyAvatar src={chat.user.avatar!} info={chat.user} />
              <div className={styles.name}>{chat.user.name}</div>
            </div>
            <ProForm
              form={newContactForm}
              onFinish={(values) => addContactClick(values)}
              className={styles.contact_form}
              submitter={{
                searchConfig: { submitText: 'Send Contact Request' },
                resetButtonProps: false,
                submitButtonProps: {
                  size: 'large',
                  style: {
                    marginTop: 12,
                    width: '100%',
                    borderRadius: 10,
                    backgroundColor: '#703eff',
                  },
                },
              }}
            >
              <ProFormTextArea
                name="text"
                label="Request Message"
                placeholder="Please enter request message"
              />
              <ProFormText
                name="alias"
                label="Set Alias"
                placeholder="Please enter alias"
              />
              <ProForm.Item name="tags" label="Add Tags">
                <CustomInputTag goNext={() => setCurrentStep(Step.ChooseTag)} />
              </ProForm.Item>
              <ProForm.Item name="switch" label="Besides Chat">
                <CustomSwitch goNext={() => setCurrentStep(Step.ChooseTag)} />
              </ProForm.Item>
            </ProForm>
          </>
        )}
        {currentStep === Step.ChooseTag && (
          <div className={styles.tags_panel}>
            <div className={styles.tags_header}>
              <span
                className={styles.cancel}
                onClick={() => setCurrentStep(Step.NewContact)}
              >
                Cancel
              </span>
              <span>Tags</span>
              <span className={styles.confirm} onClick={handleConfirmTags}>
                Confirm
              </span>
            </div>
            <div className={styles.tags_container}>
              {tagList.map((item) => {
                return (
                  <div className={styles.tag_item} key={item.id}>
                    <div className={styles.info}>
                      <TagIcon />
                      <span>{item.title}</span>
                    </div>
                    <div onClick={() => handleTagClick(item.id)}>
                      {item.choose ? <SelIcon /> : <UnSelIcon />}
                    </div>
                  </div>
                );
              })}
              <div
                className={cls(styles.tag_item, styles.add_tag)}
                onClick={() => {
                  addTagForm.resetFields();
                  setCurrentStep(Step.CreateTag);
                }}
              >
                <AddIcon />
                <span>Create Tags</span>
              </div>
            </div>
          </div>
        )}
        {currentStep === Step.CreateTag && (
          <div className={styles.tags_panel}>
            <ProForm
              className={styles.contact_form}
              submitter={false}
              form={addTagForm}
            >
              <div className={styles.tags_header}>
                <span
                  className={styles.cancel}
                  onClick={() => setCurrentStep(Step.ChooseTag)}
                >
                  Cancel
                </span>
                <span>Create Tags</span>
                <Button
                  type="link"
                  className={cls(styles.confirm, styles.create_tag_btn)} //styles.confirm}
                  htmlType="submit"
                  onClick={handleCreateTag}
                >
                  Create
                </Button>
              </div>
              <ProFormText
                name="tag_name"
                label="Tag Name"
                rules={[{ required: true }]}
                placeholder="Please enter a tag Name"
              />
            </ProForm>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AddFriendModal;
